import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import toast from 'react-hot-toast';
import { OtpInput } from '@/components/ui/otpInput';
import { Input } from '@/components/ui/input';
import { useSendForgotOtpMutation, useResetForgotPasswordMutation } from '@/store/api/authApi';
import { Eye, EyeOff } from 'lucide-react';

const OTP_RESEND_TIME = 120;

type Step = 'email' | 'reset';

const emailSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

const resetSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof emailSchema> & z.infer<typeof resetSchema>;

type PageType = 'login' | 'register' | 'forgot-password';

interface ForgotPasswordProps {
  toggleAnimation: (value: PageType) => void;
}

export function ForgotPassword({ toggleAnimation }: ForgotPasswordProps) {
  const [step, setStep] = useState<Step>('email');
  const [timer, setTimer] = useState<number>(0);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [sendOtp, { isLoading: sendingOtp, error: otpErrorResponse }] = useSendForgotOtpMutation();

  const [resetPassword, { isLoading: resetting }] = useResetForgotPasswordMutation();

  const {
    register,
    handleSubmit,
    trigger,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(step === 'email' ? emailSchema : resetSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  const handleSendOtp = async () => {
    const valid = await trigger('email');
    if (!valid) return;

    const data = getValues();

    try {
      await sendOtp({
        email: data.email.toLowerCase(),
      }).unwrap();

      toast.success('OTP sent successfully');
      setStep('reset');
      setTimer(OTP_RESEND_TIME);
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to send OTP');
    }
  };

  const clearState = () => {
    reset({
      email: '',
      password: '',
      confirmPassword: '',
    });

    setStep('email');
    setOtp('');
    setOtpError('');
    setTimer(0);
  };

  const onSubmit = async () => {
    if (otp.length !== 6) {
      setOtpError('Enter valid OTP');
      return;
    }

    const data = getValues();

    try {
      await resetPassword({
        email: data.email.toLowerCase(),
        code: otp,
        newPassword: data.password,
      }).unwrap();

      toast.success('Password reset successful');

      clearState();

      setTimeout(() => {
        toggleAnimation('login');
      }, 200);
    } catch (err: any) {
      toast.error(err?.data?.message || 'Invalid OTP');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-irish font-semibold text-center my-10">Forgot Password</h1>

      {otpErrorResponse && (
        <ErrorBanner
          type="error"
          title="OTP Error"
          message={(otpErrorResponse as any)?.data?.message || 'Failed to send OTP'}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {step === 'email' && (
          <>
            <Input type="email" placeholder="Enter your email" {...register('email')} />

            {errors.email && (
              <p className="text-sm text-destructive mt-2">{errors.email.message}</p>
            )}

            <Button
              type="button"
              onClick={handleSendOtp}
              disabled={sendingOtp}
              variant="auth"
              className="w-full mt-6"
            >
              {sendingOtp ? 'Sending OTP...' : 'Continue'}
            </Button>
          </>
        )}

        {step === 'reset' && (
          <>
            <OtpInput value={otp} onChange={setOtp} />

            {otpError && <p className="text-sm text-destructive mt-2">{otpError}</p>}

            <p className="text-sm text-muted-foreground mt-3">
              {timer > 0 ? (
                <>
                  Resend OTP in <span className="font-semibold">{timer}s</span>
                </>
              ) : (
                <span className="text-primary cursor-pointer font-semibold" onClick={handleSendOtp}>
                  Resend OTP
                </span>
              )}
            </p>

            <div className="relative mt-5">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                {...register('password')}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </Button>
            </div>

            {errors.password && (
              <p className="text-destructive text-sm">{errors.password.message}</p>
            )}

            <div className="relative mt-4">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                {...register('confirmPassword')}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </Button>
            </div>

            {errors.confirmPassword && (
              <p className="text-destructive text-sm">{errors.confirmPassword.message}</p>
            )}

            <Button type="submit" disabled={resetting} variant="auth" className="w-full mt-6">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </>
        )}
      </form>

      <div className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleAnimation('login');
          }}
          className="cursor-pointer font-medium text-[#FFB816] hover:underline"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
